import { createHttpClient } from '@adapters/HttpClient';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { apiClientWH } from '@shared/services/ApiClient';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { FiltersApiAdapter } from '@modules/Orders/List/adapters/FiltersApiAdapter';
import { TeamsApiAdapter } from '@modules/Orders/List/adapters/TeamsApiAdapter';

// Estados logísticos disponibles según la documentación del endpoint
// Solo estados: 5 (Entregado), 6 (Devuelto), 7 (Cancelado), 8 (Siniestro), 9 (Reexpedido), 10 (Destruido)
const SHIPMENT_STATUS_OPTIONS = [
  { value: '5', label: 'Entregado' },
  { value: '6', label: 'Devuelto al Remitente' },
  { value: '7', label: 'Cancelado' },
  { value: '8', label: 'Siniestro' },
  { value: '9', label: 'Reexpedido' },
  { value: '10', label: 'Destruido' },
];

type MigrationFilterValue = 'all' | 'migrated' | 'not_migrated';

interface OrdersWithoutWeightFiltersState {
  processedAt: DateRange | undefined;
  lastShipmentUpdate: DateRange | undefined;
  shipmentStatus: string[];
  shop: string[];
  courier: string[];
  client: string[];
  migrationFilter: MigrationFilterValue;
}

interface UseOrdersWithoutWeightFiltersProps {
  /** Condiciones iniciales desde query params */
  initialConditions?: FilterConditionOutput[];
  /** Callback cuando cambian los filtros */
  onChange?: (conditions: FilterConditionOutput[]) => void;
  /** Si mostrar el filtro de cliente */
  hasTeamData?: boolean;
}

/**
 * Hook para gestionar los filtros simplificados de OrdersWithoutWeight
 *
 * Convierte el estado de los filtros UI al formato FilterConditionOutput[]
 * esperado por la API y gestiona la carga de opciones dinámicas.
 */
export const useOrdersWithoutWeightFilters = ({
  initialConditions = [],
  onChange,
  hasTeamData = false,
}: UseOrdersWithoutWeightFiltersProps) => {
  // Estado de los filtros
  const [filters, setFilters] = useState<OrdersWithoutWeightFiltersState>(() => {
    // Parsear condiciones iniciales para establecer estado inicial
    return parseConditionsToState(initialConditions);
  });

  // HTTP Client y adapters
  const httpClient = createHttpClient(apiClientWH);
  const filtersAdapter = useMemo(() => new FiltersApiAdapter(httpClient), [httpClient]);
  const teamsAdapter = useMemo(() => new TeamsApiAdapter(httpClient), [httpClient]);

  // Cargar opciones dinámicas
  const { data: shops = [], isLoading: isLoadingShops } = useQuery({
    queryKey: ['orders-without-weight-filters-shops', hasTeamData],
    queryFn: async () => {
      const shops = await filtersAdapter.getShops({ includeTeam: hasTeamData });
      return shops.map((shop) => {
        // Construir descripción con nombre del team si aplica
        const description = hasTeamData && shop.team?.name ? shop.team.name : undefined;
        return {
          value: shop.id.toString(),
          label: shop.name,
          description,
        };
      });
    },
  });

  const { data: couriers = [], isLoading: isLoadingCouriers } = useQuery({
    queryKey: ['orders-without-weight-filters-couriers'],
    queryFn: async () => {
      const couriers = await filtersAdapter.getCouriers();
      return couriers.map((courier) => ({ value: courier.id.toString(), label: courier.name }));
    },
  });

  const { data: clients = [], isLoading: isLoadingClients } = useQuery({
    queryKey: ['orders-without-weight-filters-clients'],
    queryFn: async () => {
      if (!hasTeamData) return [];
      const teams = await teamsAdapter.getTeamsChild();
      return teams.map((team) => ({ value: team.id.toString(), label: team.name }));
    },
    enabled: hasTeamData,
  });

  // Convertir estado a FilterConditionOutput[]
  const convertStateToConditions = useCallback((state: OrdersWithoutWeightFiltersState): FilterConditionOutput[] => {
    const conditions: FilterConditionOutput[] = [];

    // Processed_at
    if (state.processedAt?.from && state.processedAt?.to) {
      conditions.push({
        column: 'processed_at',
        action: 'greater_equal',
        value: formatDateForAPI(state.processedAt.from),
      });
      conditions.push({
        column: 'processed_at',
        action: 'less_equal',
        value: formatDateForAPI(state.processedAt.to),
      });
    }

    // Last_shipment_update
    if (state.lastShipmentUpdate?.from && state.lastShipmentUpdate?.to) {
      conditions.push({
        column: 'last_shipment_update',
        action: 'greater_equal',
        value: formatDateForAPI(state.lastShipmentUpdate.from),
      });
      conditions.push({
        column: 'last_shipment_update',
        action: 'less_equal',
        value: formatDateForAPI(state.lastShipmentUpdate.to),
      });
    }

    // Estado de envío (shipment_status_id)
    if (state.shipmentStatus.length > 0) {
      conditions.push({
        column: 'shipment_status_id',
        action: 'in',
        value: state.shipmentStatus,
      });
    }

    // Tienda (shop_id)
    if (state.shop.length > 0) {
      conditions.push({
        column: 'shop_id',
        action: 'in',
        value: state.shop,
      });
    }

    // Courier (courier_id)
    if (state.courier.length > 0) {
      conditions.push({
        column: 'courier_id',
        action: 'in',
        value: state.courier,
      });
    }

    // Cliente (team_id)
    if (state.client.length > 0) {
      conditions.push({
        column: 'team_id',
        action: 'in',
        value: state.client,
      });
    }

    if (state.migrationFilter === 'migrated') {
      conditions.push({
        column: 'is_migrated',
        action: 'is_not_null',
        value: null,
      });
    } else if (state.migrationFilter === 'not_migrated') {
      conditions.push({
        column: 'is_migrated',
        action: 'is_null',
        value: null,
      });
    }

    return conditions;
  }, []);

  // Actualizar filtros y notificar cambio
  const updateFilters = useCallback(
    (updates: Partial<OrdersWithoutWeightFiltersState>) => {
      setFilters((prev) => {
        const newState = { ...prev, ...updates };
        const conditions = convertStateToConditions(newState);
        onChange?.(conditions);
        return newState;
      });
    },
    [convertStateToConditions, onChange],
  );

  // Handlers individuales
  const handleProcessedAtChange = useCallback(
    (range: DateRange | undefined) => {
      updateFilters({ processedAt: range });
    },
    [updateFilters],
  );

  const handleLastShipmentUpdateChange = useCallback(
    (range: DateRange | undefined) => {
      updateFilters({ lastShipmentUpdate: range });
    },
    [updateFilters],
  );

  const handleShipmentStatusChange = useCallback(
    (values: string[]) => {
      updateFilters({ shipmentStatus: values });
    },
    [updateFilters],
  );

  const handleShopChange = useCallback(
    (values: string[]) => {
      updateFilters({ shop: values });
    },
    [updateFilters],
  );

  const handleCourierChange = useCallback(
    (values: string[]) => {
      updateFilters({ courier: values });
    },
    [updateFilters],
  );

  const handleClientChange = useCallback(
    (values: string[]) => {
      updateFilters({ client: values });
    },
    [updateFilters],
  );

  const handleMigrationFilterChange = useCallback(
    (value: MigrationFilterValue) => {
      updateFilters({ migrationFilter: value });
    },
    [updateFilters],
  );

  // Limpiar todos los filtros
  const clearAllFilters = useCallback(() => {
    const emptyState: OrdersWithoutWeightFiltersState = {
      processedAt: undefined,
      lastShipmentUpdate: undefined,
      shipmentStatus: [],
      shop: [],
      courier: [],
      client: [],
      migrationFilter: 'all',
    };
    setFilters(emptyState);
    onChange?.([]);
  }, [onChange]);

  // Sincronizar con condiciones iniciales cuando cambien
  useEffect(() => {
    const newState = parseConditionsToState(initialConditions);
    setFilters(newState);
  }, [initialConditions]);

  // Obtener condiciones actuales
  const currentConditions = useMemo(() => convertStateToConditions(filters), [filters, convertStateToConditions]);

  return {
    // Estado de filtros
    filters,

    // Handlers
    handleProcessedAtChange,
    handleLastShipmentUpdateChange,
    handleShipmentStatusChange,
    handleShopChange,
    handleCourierChange,
    handleClientChange,
    handleMigrationFilterChange,
    clearAllFilters,

    // Opciones disponibles
    shipmentStatusOptions: SHIPMENT_STATUS_OPTIONS,
    shopOptions: shops,
    courierOptions: couriers,
    clientOptions: clients,

    // Estados de carga
    isLoadingShops,
    isLoadingCouriers,
    isLoadingClients,

    // Condiciones actuales en formato API
    currentConditions,
  };
};

/**
 * Parsea condiciones FilterConditionOutput[] al estado de filtros
 */
function parseMigrationFilterFromConditions(conditions: FilterConditionOutput[]): MigrationFilterValue {
  const m = conditions.find((c) => c.column === 'is_migrated' || c.column === 'migration_id_mappings');
  if (!m) return 'all';
  if (m.action === 'is_not_null') return 'migrated';
  if (m.action === 'is_null') return 'not_migrated';
  if (m.action === 'equals') {
    const v = Array.isArray(m.value) ? m.value[0] : m.value;
    if (v === true || v === 1) return 'migrated';
    if (v === false || v === 0) return 'not_migrated';
    const s = String(v ?? '')
      .trim()
      .toLowerCase();
    if (['true', '1', 'yes', 'si', 'sí', 'y'].includes(s)) return 'migrated';
    if (['false', '0', 'no', 'n'].includes(s)) return 'not_migrated';
  }
  return 'all';
}

function parseConditionsToState(conditions: FilterConditionOutput[]): OrdersWithoutWeightFiltersState {
  const state: OrdersWithoutWeightFiltersState = {
    processedAt: undefined,
    lastShipmentUpdate: undefined,
    shipmentStatus: [],
    shop: [],
    courier: [],
    client: [],
    migrationFilter: parseMigrationFilterFromConditions(conditions),
  };

  // Agrupar condiciones por columna
  const byColumn: Record<string, FilterConditionOutput[]> = {};
  conditions.forEach((condition) => {
    if (!byColumn[condition.column]) {
      byColumn[condition.column] = [];
    }
    byColumn[condition.column].push(condition);
  });

  // Helper para parsear rangos de fechas
  const parseDateRange = (column: string): DateRange | undefined => {
    const dateConditions = byColumn[column] || [];
    const dateFrom = dateConditions.find((c) => c.action === 'greater_equal');
    const dateTo = dateConditions.find((c) => c.action === 'less_equal');
    if (dateFrom?.value && dateTo?.value) {
      return {
        from: new Date(dateFrom.value),
        to: new Date(dateTo.value),
      };
    }
    return undefined;
  };

  // Fechas
  state.processedAt = parseDateRange('processed_at');
  state.lastShipmentUpdate = parseDateRange('last_shipment_update');

  // Arrays con operador 'in'
  const getArrayValue = (column: string): string[] => {
    const condition = byColumn[column]?.find((c) => c.action === 'in');
    if (condition?.value) {
      return Array.isArray(condition.value) ? condition.value : [condition.value];
    }
    return [];
  };

  state.shipmentStatus = getArrayValue('shipment_status_id');
  state.shop = getArrayValue('shop_id');
  state.courier = getArrayValue('courier_id');
  state.client = getArrayValue('team_id');

  return state;
}

/**
 * Formatea una fecha para la API (formato YYYY-MM-DD)
 * Usa métodos locales para evitar problemas de timezone
 */
function formatDateForAPI(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
